import { forwardRef, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import MyButton from "../myButton";
import "./styles.scss";
import Logo from "../logo";
import { scrollToForm } from "../../utils/scrolls";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";

const Header = forwardRef<HTMLElement>((props, ref) => {
	const location = useLocation();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [activeHash, setActiveHash] = useState(location.hash);

	const toggleDrawer = (newOpen: boolean) => () => {
		setOpen(newOpen);
	};

	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	};

	const navlinks = useMemo(
		() => [
			{ label: "الرئيسية", to: "/", onClick: scrollToTop },
			{ label: "الخدمات", hash: "#our-services" },
			{ label: "ساعات العمل", hash: "#working-hours" },
			{ label: "الشفافية", hash: "#transparency" },
			{ label: "عن الخدمة", to: "/about" },
			{ label: "تواصل معنا", hash: "#form-card" },
		],
		[],
	);

	useEffect(() => {
		setActiveHash(location.hash);
	}, [location.hash]);

	useEffect(() => {
		const hashLinks = navlinks.filter((navlink) => navlink.hash);

		const updateActiveHash = () => {
			const header = document.querySelector(".header") as HTMLElement | null;
			const headerHeight = header?.getBoundingClientRect().height ?? 0;
			const activationLine = headerHeight + 80;

			let currentHash = "";

			for (const navlink of hashLinks) {
				if (!navlink.hash) continue;

				const section = document.querySelector(navlink.hash);
				if (!section) continue;

				const rect = section.getBoundingClientRect();
				if (rect.top <= activationLine && rect.bottom > activationLine) {
					currentHash = navlink.hash;
					break;
				}
			}

			if (!currentHash && window.scrollY < 80) {
				currentHash = "";
			}

			setActiveHash((prev) => (prev === currentHash ? prev : currentHash));
		};

		updateActiveHash();
		window.addEventListener("scroll", updateActiveHash, { passive: true });
		window.addEventListener("resize", updateActiveHash);

		return () => {
			window.removeEventListener("scroll", updateActiveHash);
			window.removeEventListener("resize", updateActiveHash);
		};
	}, [navlinks]);

	const handleNavlinkClick = (navlink: (typeof navlinks)[number]) => {
		if (navlink.to) {
			navigate(navlink.to);
			navlink.onClick?.();
			setActiveHash("");
		} else if (navlink.hash) {
			setActiveHash(navlink.hash);
			scrollToForm(navigate, navlink.hash);
		}
		setOpen(false);
	};

	return (
		<header ref={ref} className="header">
			<div className="header-content">
				<div className="header-right-container">
					<button className="header-mobile-menu-btn" onClick={toggleDrawer(true)}>
						<MenuIcon className="header-mobile-menu-btn-icon" />
					</button>
					<Logo onClick={scrollToTop} />
				</div>

				<div className="header-left-container">
					<div className="header-navlinks-container">
						{navlinks.map((navlink, i) => {
							const isActive = navlink.to ? location.pathname === navlink.to && !activeHash : activeHash === navlink.hash;

							return navlink.to ? (
								<NavLink
									key={i}
									to={navlink.to}
									onClick={() => {
										navlink.onClick?.();
										setActiveHash("");
									}}
									className={`header-navlink ${isActive ? "active-tab" : ""}`}
								>
									{navlink.label}
								</NavLink>
							) : (
								<button
									key={i}
									type="button"
									className={`header-navlink ${isActive ? "active-tab" : ""}`}
									onClick={() => {
										setActiveHash(navlink.hash || "");
										scrollToForm(navigate, navlink.hash);
									}}
								>
									{navlink.label}
								</button>
							);
						})}
					</div>

					<MyButton onClick={() => scrollToForm(navigate, "#form-card")}>طلب مساعدة</MyButton>
				</div>
			</div>

			<Drawer open={open} anchor="right" onClose={toggleDrawer(false)} dir="rtl">
				<Box sx={{ width: 250 }} role="presentation">
					<div style={{ padding: 20 }}>
						<Logo
							onClick={() => {
								scrollToTop();
								setOpen(false);
							}}
						/>
					</div>
					<List>
						{navlinks.map((navlink, index) => {
							const isActive = navlink.to ? location.pathname === navlink.to && !activeHash : activeHash === navlink.hash;

							return (
								<ListItem key={index} disablePadding>
									<ListItemButton dir="rtl" onClick={() => handleNavlinkClick(navlink)}>
										<label
											style={{
												textAlign: "start",
												color: "var(--textColor)",
												fontFamily: "inherit",
												cursor: "pointer",
												padding: "7px 0",
												fontWeight: isActive ? 600 : undefined,
												opacity: isActive ? 1 : 0.7,
												fontSize: 16,
											}}
										>
											{navlink.label}
										</label>
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>
				</Box>
			</Drawer>
		</header>
	);
});

export default Header;
